/**
 * ID generation for spaces and boards — mirrors generate-tile-id.ts discipline.
 *
 * Patterns (from constants.ts):
 *   SPACE_ID_PATTERN  /^space_[a-z0-9]{8,12}$/
 *   BOARD_ID_PATTERN  /^board_[a-z0-9]{6,16}$/
 *
 * Strategy: base-36 timestamp + random salt, lower-cased, trimmed to fit.
 * Collision probability within a MAX_SPACES=8 envelope is negligible.
 *
 * Pure — no side-effects beyond Date.now() and Math.random().
 */
import { SPACE_ID_PATTERN, BOARD_ID_PATTERN } from 'briefpane/schema';

/**
 * Generate a unique space id matching SPACE_ID_PATTERN /^space_[a-z0-9]{8,12}$/.
 * Callers must check for id-collision (ops return 'id-collision' if one exists).
 */
export function generateSpaceId(): string {
  const ts = Date.now().toString(36).slice(-6).padStart(6, '0');
  const rand = Math.random().toString(36).slice(2, 6).padStart(4, '0');
  const id = `space_${ts}${rand}`;
  if (!SPACE_ID_PATTERN.test(id)) {
    // Deterministic fallback
    const hex = Date.now().toString(16).slice(-6);
    const rnd = Math.floor(Math.random() * 0xffffff).toString(16).padStart(6, '0');
    return `space_${hex}${rnd}`.slice(0, 18);
  }
  return id;
}

/**
 * Generate a unique board id matching BOARD_ID_PATTERN /^board_[a-z0-9]{6,16}$/.
 * Used when creating a new space (new board id) or duplicating a space (new board).
 */
export function generateBoardId(): string {
  const ts = Date.now().toString(36).slice(-8).padStart(8, '0');
  const rand = Math.random().toString(36).slice(2, 6).padStart(4, '0');
  const id = `board_${ts}${rand}`;
  if (!BOARD_ID_PATTERN.test(id)) {
    const hex = Date.now().toString(16).slice(-6);
    const rnd = Math.floor(Math.random() * 0xffffff).toString(16).padStart(6, '0');
    return `board_${hex}${rnd}`.slice(0, 22);
  }
  return id;
}
